import { Link } from "react-router-dom";
import { APP_NAME } from "../lib/sport.js";

/**
 * The privacy policy, and the URL Google's OAuth consent screen requires.
 *
 * Written from what the schema actually stores rather than from a template. A policy that
 * describes a different app is worse than none: it is the document people are asked to
 * trust at the moment they hand over their Google account.
 *
 * If the schema changes, this changes. The paragraph about who can see your matches is the
 * one that matters most — a community leaderboard is a public-by-design product, and
 * nobody should discover that after joining.
 */

/** Set as a repository variable. Google's consent screen needs a reachable contact. */
const CONTACT = import.meta.env["VITE_CONTACT_EMAIL"] as string | undefined;

const UPDATED = "1 October 2026";

export function Privacy() {
  return (
    <main
      className="mx-auto max-w-2xl px-4 pb-24"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 2.5rem)" }}
    >
      <div className="micro mb-3">{APP_NAME}</div>
      <h1 className="font-display mb-2 text-3xl leading-tight" style={{ fontWeight: 800 }}>
        Privacy
      </h1>
      <p className="micro mb-8">Last updated {UPDATED}</p>

      <div className="tramline mb-8" />

      <Section title="The short version">
        <p>
          {APP_NAME} keeps a skill rating for the Prestige Hillside Gateway community. It stores
          your name, your email address, and the matches you play — and, if you give them, your flat
          number and phone number. It has no analytics, no advertising, no tracking of any kind, and
          it sells nothing to anybody. Everyone else in your community can see your name, your
          rating and your match history — that is what a leaderboard is. Your flat number and phone
          number only organisers and admins can see.
        </p>
      </Section>

      <Section title="What is stored, and why">
        <Row label="Email address">
          Provided by Google when you sign in. It identifies your account and nothing else. It is
          never shown to other members and never used to email you.
        </Row>
        <Row label="Name and profile picture">
          Taken from your Google account when you first sign in. Both are shown to other members.
          Only the <em>address</em> of the picture is stored — the image itself stays with Google.
        </Row>
        <Row label="Flat number and phone number">
          Both optional, asked for when you join and changeable on your profile at any time. Only
          you, organisers and admins can see them — never other members, and never on your player
          page, the leaderboard or an entry list. They are there so an organiser can reach you about
          a match. A tournament can ask for both before you enter it, or before you enter a child;
          otherwise you never have to give them.
        </Row>
        <Row label="Your self-assessed level">
          What you tell us when you ask to join. It sets your starting rating and is not shown to
          anyone else.
        </Row>
        <Row label="Matches">
          Who played, which side, the score of every game, and when. Visible to every member of your
          community.
        </Row>
        <Row label="Ratings and rating history">
          Your rating after every match, and the numbers it was calculated from. Your current rating
          is on the leaderboard; the history behind it is visible to members.
        </Row>
        <Row label="Administrative actions">
          When an admin approves a member, enters a match on someone&rsquo;s behalf, or confirms
          one, that action is recorded with who did it. This exists so that administrative power
          over your rating is accountable rather than invisible.
        </Row>
      </Section>

      <Section title="Who else touches it">
        <p className="mb-3">
          Three companies, all acting as infrastructure. None of them is sent your data for their
          own purposes.
        </p>
        <Row label="Google">
          Handles sign-in. It tells us your email, name and profile picture. We never receive your
          password and can never act on your Google account.
        </Row>
        <Row label="Supabase">
          Hosts the database and the sign-in system. Everything described above lives there.
        </Row>
        <Row label="Cloudflare">
          Serves the app itself and keeps standard server logs, which include IP addresses.
        </Row>
      </Section>

      <Section title="Cookies and tracking">
        <p>
          There are none. No analytics, no advertising, no third-party scripts. Your browser stores
          one sign-in token so you are not asked to sign in on every visit — that is local to your
          device, and signing out removes it.
        </p>
      </Section>

      <Section title="How long it is kept">
        <p>
          Matches are kept for as long as the community exists. Every rating is calculated by
          replaying the match log, so deleting a match would alter other people&rsquo;s ratings as
          well as your own — a match is shared history, not solely yours.
        </p>
      </Section>

      <Section title="Removing your data">
        <p className="mb-3">
          Ask an admin of your community and they will remove your account. Your name, email,
          profile picture, flat number, phone number and rating go with it. You can clear your flat
          number and phone number yourself, on your profile, at any time.
        </p>
        <p>
          Matches you played in remain, because they are also the record of the people you played
          against. Removal is a manual step today rather than a button; if you have asked and
          nothing happened, use the contact below.
        </p>
      </Section>

      <Section title="Children">
        <p className="mb-3">
          Children do not sign in. A parent, or an organiser, can add a child without a login so
          that they can be entered in kids&rsquo; events. Adding one is recorded, with who did it.
        </p>
        <Row label="What is kept about a child">
          Their name, whether they are a boy or a girl, that they play as a junior, who looks after
          them, the events they are entered in, and the matches they play. No email address, no date
          of birth, no age, no photo, no flat number or phone number.
        </Row>
        <Row label="Who can see it">
          Their name and results are visible to members, like anybody&rsquo;s. Who looks after them
          is visible only to those people and to organisers. When an organiser needs to reach a
          child, they see the guardian&rsquo;s flat number and phone number instead. Kids&rsquo;
          events do not count towards ratings, so a child who plays only in those has no rating and
          is not on the leaderboard.
        </Row>
        <Row label="Removing a child">
          Ask an admin, as for your own account. Matches they played remain, because they are also
          the record of the children they played against.
        </Row>
      </Section>

      <Section title="Contact">
        {CONTACT ? (
          <p>
            <a className="underline" href={`mailto:${CONTACT}`}>
              {CONTACT}
            </a>
          </p>
        ) : (
          <p>Contact an admin of your community.</p>
        )}
      </Section>

      <div className="tramline mb-8 mt-10" />
      <Link className="micro underline" to="/">
        Back
      </Link>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      {/* The section label the rest of the app uses — mono, upper case, on a rule. Set in
          the condensed display face at this size it read as cramped rather than headline. */}
      <h2
        className="font-mono mb-3 border-t-2 pt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink"
        style={{ borderColor: "var(--color-line)" }}
      >
        {title}
      </h2>
      <div className="text-ink-muted">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-t py-3" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="micro mb-1" style={{ color: "var(--color-ink)" }}>
        {label}
      </div>
      <p>{children}</p>
    </div>
  );
}
